import React, { useState, useEffect } from 'react';
import { Megaphone, ChevronRight, Calendar, Loader2 } from 'lucide-react';
import { fetchAllNotices } from '../../services/adminService';
import { Notice } from '../../types';
import { useLanguage } from '../../context/LanguageContext';
import { t, Language } from '../../i18n';
import { formatLocalDateTime } from '../../services/formatService';

export const NoticeListView: React.FC = () => {
    const { lang } = useLanguage();
    const [notices, setNotices] = useState<Notice[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [expandedId, setExpandedId] = useState<string | null>(null);

    useEffect(() => {
        const loadNotices = async () => {
            try {
                const data = await fetchAllNotices();
                // Show all notices regardless of is_published or date range as requested
                setNotices(data);
            } catch (e) {
                console.error('Failed to load notices:', e);
            } finally {
                setIsLoading(false);
            }
        };
        loadNotices();
    }, []);

    if (isLoading) {
        return (
            <div className="py-20 flex flex-col items-center justify-center gap-4">
                <Loader2 className="w-8 h-8 text-slate-300 animate-spin" />
                <span className="text-[10px] font-black uppercase tracking-widest text-slate-300">
                    {t(lang as Language, 'loading')}
                </span>
            </div>
        );
    }

    if (notices.length === 0) {
        return (
            <div className="py-32 flex flex-col items-center justify-center text-center px-10">
                <div className="w-20 h-20 bg-slate-50 rounded-[2.5rem] flex items-center justify-center text-slate-200 mb-6">
                    <Megaphone size={40} />
                </div>
                <h3 className="text-lg font-black text-slate-900 mb-2 uppercase tracking-tight">
                    {t(lang as Language, 'noNotices')}
                </h3>
                <p className="text-xs font-bold text-slate-400 leading-relaxed uppercase tracking-wider">
                    {t(lang as Language, 'noNoticesDesc')}
                </p>
            </div>
        );
    }

    return (
        <div className="pb-20">
            <div className="divide-y divide-slate-50">
                {notices.map((notice) => {
                    const isExpanded = expandedId === notice.id;
                    
                    const title = 
                        lang === 'ko' ? notice.title_ko : 
                        // lang === 'ja' ? (notice.title_ja || notice.title_en) :
                        // lang === 'es' ? (notice.title_es || notice.title_en) :
                        // lang === 'fr' ? (notice.title_fr || notice.title_en) :
                        notice.title_en;

                    const description = 
                        lang === 'ko' ? notice.description_ko : 
                        // lang === 'ja' ? (notice.description_ja || notice.description_en) :
                        // lang === 'es' ? (notice.description_es || notice.description_en) :
                        // lang === 'fr' ? (notice.description_fr || notice.description_en) :
                        notice.description_en;

                    return (
                        <div key={notice.id} className="bg-white overflow-hidden transition-all duration-300">
                            <button
                                onClick={() => setExpandedId(isExpanded ? null : notice.id)}
                                className={`w-full flex items-start gap-5 p-6 text-left transition-colors ${isExpanded ? 'bg-slate-50/50' : 'hover:bg-slate-50/30'}`}
                            >
                                <div className={`mt-1 w-10 h-10 rounded-2xl flex items-center justify-center shrink-0 transition-all duration-500 ${isExpanded ? 'bg-slate-900 text-white shadow-xl rotate-12' : 'bg-slate-100 text-slate-400'}`}>
                                    <Megaphone size={18} />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <h4 className={`text-sm font-black leading-snug transition-colors ${isExpanded ? 'text-slate-900' : 'text-slate-600'}`}>
                                        {title}
                                    </h4>
                                    <div className="flex items-center gap-2 mb-1">
                                        <Calendar size={10} className="text-slate-300" />
                                        <span className="text-[10px] font-black text-slate-300 uppercase tracking-widest">
                                            {formatLocalDateTime(notice.created_at || '', lang)}
                                        </span>
                                    </div>
                                </div>
                                <div className={`mt-4 transition-transform duration-500 ${isExpanded ? 'rotate-90' : ''}`}>
                                    <ChevronRight size={18} className="text-slate-300" />
                                </div>
                            </button>

                            <div 
                                className={`transition-all duration-500 ease-[0.32,0.72,0,1] overflow-hidden ${isExpanded ? 'max-h-[2000px] opacity-100' : 'max-h-0 opacity-0'}`}
                            >
                                <div className="px-6 pb-8 pl-[84px] pr-8">
                                    <div className="prose prose-sm max-w-none">
                                        <p className="text-sm font-bold text-slate-500 leading-relaxed whitespace-pre-wrap">
                                            {description}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};
